import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DateRangeInterface, Service } from 'webapp-types';
import {
	BillingFeature,
	DataPerformanceSummaryType,
	Direction,
	PopupEvent,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformancePopupSummaryComponent from 'webapp/components/data-performance/popup-summary';
import DataPerformanceSummaryBaseComponent from 'webapp/components/data-performance/summary-base';
import DataPerformanceSummaryIndicatorComponent from 'webapp/components/data-performance/summary/indicator';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';

import type { MetricSetup } from 'webapp/models-custom';
import type DataPerformanceSummaryDynamic from 'webapp/models-custom/data-performance-summary/dynamic';
import type DateRange from 'webapp/models-custom/date-range';

import type { DataPerformanceSummaryObject } from 'webapp/services/model-manager/data-performance-summaries';

interface Args {
	chartState: MetricInsightsChartState;
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	forceLoadingState: boolean;
}

export default class MetricDetailsTopDatapointsPerformanceSummaryComponent extends DataPerformanceSummaryBaseComponent<Args> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	@tracked isPopupOpen = false;

	private forceNewRequest = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.resetTrend();

		this.eventManagerService.on(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this, () => {
			next(() => {
				this.forceNewRequest = true;
			});
		});
	}

	get object() {
		return this.dataPerformanceSummaryObject;
	}

	get dataPerformanceSummaryObject(): DataPerformanceSummaryObject | null {
		const metricSetup = this.args.metricSetup,
			metricSetupData = metricSetup.metricSetupData;
		if (!metricSetup.metric || !metricSetupData) {
			return null;
		}

		const dateRangeFormula: DateRangeInterface.FormulaInterface | undefined =
			this.args.dateRange?.createCopy()?.formula;

		if (!dateRangeFormula) {
			return null;
		}

		if (dateRangeFormula.granulation) {
			dateRangeFormula.granulation.isWholeRange = true;
			dateRangeFormula.granulation.multiplier = null;
			dateRangeFormula.granulation.timeUnit = null;
		}

		return {
			objectType: DataPerformanceSummaryType.DYNAMIC,
			dataSelection: {
				metricSetup: metricSetupData,
				dateRange: dateRangeFormula,
			},
		};
	}

	get isDataIncomplete() {
		return (
			this.args.chartState.isVisualizationModuleDataLoading || !!this.args.forceLoadingState
		);
	}

	get isDataEmpty() {
		return this.args.chartState.isDataEmpty;
	}

	get preventOpen() {
		return this.model?.generativeAIEnabled && this.isDataIncompleteOrEmpty;
	}

	get trendClass() {
		const noDataClass = this.isDataIncomplete ? 'unavailable' : 'disabled';
		return this.isDataIncompleteOrEmpty ? noDataClass : this.model?.trend;
	}

	get showCard() {
		return this.mmDataPerformanceSummariesService.showInUI;
	}

	get trendDescription() {
		if (!this.model?.trend) {
			return '';
		}
		return this.mmDataPerformanceSummariesService.getTrendDescription(this.model.trend);
	}

	// Elsewhere tapping without the feature offers an upgrade (or the demo popup); the mobile drawer shows the trend only.
	get canOpenSummary(): boolean {
		if (!this.metricDetailsService.isMobileMode) {
			return true;
		}
		return this.billingService.generativeAIEnabled && !this.accountService.isDemoAccount;
	}

	@action
	refreshPerformanceSummary() {
		const model = this.model as DataPerformanceSummaryDynamic | null;
		const forceLoading = model && !model.summariesLoading;

		if (forceLoading) {
			model.summariesLoading = true;
		}
		next(() => {
			if (forceLoading) {
				model.summariesLoading = false;
			}

			if (
				model &&
				!this.isDataIncompleteOrEmpty &&
				(model.requestedParamHash !== model.paramHash || this.forceNewRequest)
			) {
				this.forceNewRequest = false;
				model.checkAndLoadAdditionalData();
			}
		});
	}

	@action
	openSummary(e: PointerEvent): void {
		if (this.accountService.handleDemo()) return;
		if (this.preventOpen) return;
		if (!this.billingService.checkIfCan(BillingFeature.GENERATIVE_AI, true)) return;
		if (this.model && this.aiChatService.openGenieForPerformanceSummary(this.model)) return;
		if (this.popupService.isOpen(DataPerformancePopupSummaryComponent)) return;

		const target = e.currentTarget as HTMLElement | null;
		if (!target) return;

		const popup = this.popupService.createContext(
			DataPerformancePopupSummaryComponent,
			target,
			{
				popupClass: 'popup-type-data-performance-summary-context',
				directions: [Direction.RIGHT, Direction.LEFT, Direction.DOWN, Direction.UP],
				allowStacked: true,
			},
		);

		const model = this.model;
		if (popup && model) {
			// Genie would take the mobile app out of the drawer
			popup.open({ model, hideGenieInput: this.metricDetailsService.isMobileMode });

			this.isPopupOpen = true;

			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isPopupOpen = false;
				}
			});

			this.mmDataPerformanceSummariesService.trackEvent(
				TrackingEvent.DATA_PERFORMANCE_SUMMARY_POPUP_OPENED,
				model,
				{ origin: 'metrics_detail' },
			);
		}
	}

	@action
	teardownView(): void {
		this.eventManagerService.off(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this);
	}

	override willDestroy() {
		super.willDestroy();
		const model = this.model as DataPerformanceSummaryDynamic | null;
		model?.summariesXHR?.abort();
	}

	<template>
		{{#if this.showCard}}
			<div
				class="top-datapoints__card top-datapoints__card--performance-summary"
				{{willDestroy this.teardownView}}
			>
				<div class="top-datapoints__card-label">
					<CommonUiIconComponent @name="Sparkles" @size="sm" />
					<span class="top-datapoints__card-label-text">Performance summary</span>
				</div>
				{{#if this.model}}
					{{#if this.model.isTypeDynamic}}
						<div
							{{didInsert this.refreshPerformanceSummary}}
							{{didUpdate
								this.refreshPerformanceSummary
								this.isDataIncomplete
								this.isDataEmpty
							}}
						></div>
					{{/if}}
					{{#if this.isDataIncomplete}}
						<div class="top-datapoints__perf-box top-datapoints__perf-box--loading">
							<CommonUiIconComponent
								@name="PerformanceUpOld"
								@size="lg"
								@class="top-datapoints__perf-skeleton-icon"
							/>
							<span class="top-datapoints__perf-skeleton-text"></span>
						</div>
					{{else if this.canOpenSummary}}
						<div
							role="button"
							class="top-datapoints__perf-box trend--{{this.trendClass}}
								{{if this.isPopupOpen 'active'}}"
							{{on "click" this.openSummary}}
						>
							<DataPerformanceSummaryIndicatorComponent
								@trend={{this.model.trend}}
								@text={{this.trendDescription}}
								@size="lg"
							/>
							<CommonUiIconComponent
								@name="ChevronDown"
								@size="sm"
								@class="top-datapoints__perf-chevron"
							/>
						</div>
					{{else}}
						<div class="top-datapoints__perf-box trend--{{this.trendClass}}">
							<DataPerformanceSummaryIndicatorComponent
								@trend={{this.model.trend}}
								@text={{this.trendDescription}}
								@size="lg"
							/>
						</div>
					{{/if}}
				{{else}}
					<div class="top-datapoints__perf-box top-datapoints__perf-box--loading">
						<CommonUiIconComponent
							@name="PerformanceUpOld"
							@size="lg"
							@class="top-datapoints__perf-skeleton-icon"
						/>
						<span class="top-datapoints__perf-skeleton-text"></span>
					</div>
				{{/if}}
			</div>
		{{/if}}
	</template>
}
